/**
 * Propostas feitas pela plataforma: os itens de cotação que viraram proposta
 * numa seguradora (migration 225). Diferente de `seguros_propostas_v`, que é
 * a carteira sincronizada da seguradora e fica com o super admin, estas são
 * do vendedor: a RLS de `seguros_cotacoes`/`seguros_cotacoes_itens` (migration
 * 182, `get_manager_scope_users`) dá a cada um as suas, ao gerente as da
 * equipe e ao super admin todas — a mesma régua do consórcio. Nenhum filtro
 * por usuário aqui: seria reimplementar autorização no cliente.
 *
 * O `as any` no `.from` é o de `cotacaoPersistencia.ts`: os tipos gerados
 * ainda não conhecem `seguros_cotacoes*`.
 */

import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import type { DocumentoEnviado } from "./cotacao/DocumentosDaProposta";

export interface PropostaPlataforma {
  cotacaoId: number;
  seguradora: string;
  numero: string;
  status: "reservada" | "registrada" | "erro";
  enviadaEm: string | null;
  erro: string | null;
  documentos: DocumentoEnviado[];
  premioMensal: number | null;
  contatoId: number | null;
  contatoNome: string | null;
  cpf: string | null;
  vendedorNome: string | null;
}

interface Linha {
  cotacao_id: number;
  seguradora: string;
  proposta_numero: string;
  proposta_status: PropostaPlataforma["status"];
  proposta_enviada_em: string | null;
  proposta_erro: string | null;
  proposta_documentos: DocumentoEnviado[] | null;
  resultado: { premioTotal?: number | null } | null;
  seguros_cotacoes: {
    contact_id: number | null;
    perfil: { nome?: string; cpf?: string } | null;
    contacts: { details: { name?: string; cpf?: string } | null } | null;
    users: { name: string | null } | null;
  } | null;
}

const SELECT =
  "cotacao_id, seguradora, proposta_numero, proposta_status, proposta_enviada_em, proposta_erro, proposta_documentos, resultado, " +
  "seguros_cotacoes!inner(contact_id, perfil, contacts(details), users(name))";

export const paraPropostaPlataforma = (l: Linha): PropostaPlataforma => {
  const c = l.seguros_cotacoes;
  return {
    cotacaoId: l.cotacao_id,
    seguradora: l.seguradora,
    numero: l.proposta_numero,
    status: l.proposta_status,
    enviadaEm: l.proposta_enviada_em,
    erro: l.proposta_erro,
    documentos: Array.isArray(l.proposta_documentos) ? l.proposta_documentos : [],
    premioMensal: l.resultado?.premioTotal ?? null,
    contatoId: c?.contact_id ?? null,
    // A proposta leva a identidade da pessoa; o contato é o fallback.
    contatoNome: c?.perfil?.nome?.trim() || c?.contacts?.details?.name || null,
    cpf: c?.perfil?.cpf || c?.contacts?.details?.cpf || null,
    vendedorNome: c?.users?.name ?? null,
  };
};

/** As propostas visíveis para quem está logado; `contatoId` restringe a uma pessoa. */
export const carregarPropostasPlataforma = async (contatoId?: number | string): Promise<PropostaPlataforma[]> => {
  let q = supabase
    .from("seguros_cotacoes_itens" as any)
    .select(SELECT)
    .not("proposta_numero", "is", null)
    .order("proposta_enviada_em", { ascending: false, nullsFirst: false })
    .limit(500);
  if (contatoId != null) q = q.eq("seguros_cotacoes.contact_id", Number(contatoId));
  const { data, error } = await q;
  if (error) throw error;
  return ((data ?? []) as unknown as Linha[]).map(paraPropostaPlataforma);
};

/** Os dois PDFs que a MAG exige para analisar (ver `DocumentosDaProposta`). */
export const documentosCompletos = (p: PropostaPlataforma) =>
  ["ComprovanteAssinaturaEletronica", "PropostaIndividual"].every((t) => (p.documentos ?? []).some((d) => d?.tipo === t));
